/**
 * JobsPage.js — /jobs
 *
 * Lists all jobs stored in NDORFlow with the current user's fit scores.
 * Provides an "Add Job" dialog and navigation to individual job detail
 * pages.
 *
 * RC 1.25.J — lightweight filters + pagination (company ilike, location
 * ilike, source_kind eq, page, page_size).
 *
 * RC 1.25.J.1 — filter + page state persisted in the URL query string,
 * not localStorage. The URL is the single source of truth for
 * `company`, `location`, `source_kind`, and `page`; reloading,
 * refreshing, and back-navigating from Job Detail all preserve the
 * exact slice. Text inputs stay locally-controlled while typing and
 * flush to the URL after a 350ms debounce so we don't spam the back
 * stack.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { cachedFetch, invalidate as cacheInvalidate, peek, subscribe as cacheSubscribe, KEYS } from '@/lib/listCache';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Briefcase, MapPin, Building2, Loader2, Sparkles, Package,
  ChevronDown, ChevronLeft, ChevronRight, X,
} from 'lucide-react';
import AddJobDialog from '@/components/AddJobDialog';
import JobsPreferencesHydrationBanner from '@/components/JobsPreferencesHydrationBanner';
import MatchExplanationPopover from '@/components/MatchExplanationPopover';
import DecisionAdvicePopover from '@/components/DecisionAdvicePopover';
import { Switch } from '@/components/ui/switch';

/** Human-readable decision labels for the badge. */
const DECISION_BADGES = {
  reject: { label: 'Reject', className: 'bg-ndor-error-light text-ndor-error border-ndor-error/20' },
  hold: { label: 'Hold', className: 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20' },
  shortlist: { label: 'Shortlist', className: 'bg-ndor-success-light text-ndor-success border-ndor-success/20' },
  apply_with_repositioning: { label: 'Tailor application', className: 'bg-ndor-info-light text-ndor-info border-ndor-info/20' },
};

/**
 * RC 1.27 — Match label badge tones. Keyed by the tier portion of
 * `preference_scoring.match_label` (i.e. "Strong match",
 * "Good match", "Low relevance"). The "(relocation required)"
 * suffix keeps the same tone so the tier is the legible signal.
 */
const MATCH_TIER_TONE = {
  'Strong match':  'bg-ndor-success-light text-ndor-success border-ndor-success/20',
  'Good match':    'bg-ndor-info-light    text-ndor-info    border-ndor-info/20',
  'Low relevance': 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20',
};

function matchTierOf(label) {
  if (!label) return null;
  if (label.startsWith('Strong match'))  return 'Strong match';
  if (label.startsWith('Good match'))    return 'Good match';
  if (label.startsWith('Low relevance')) return 'Low relevance';
  return null;
}

/** Colour + label for each application-pack status. */
const PACK_STATUS_BADGES = {
  draft:     { label: 'Draft',     className: 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/20' },
  ready:     { label: 'Ready',     className: 'bg-ndor-info-light text-ndor-info border-ndor-info/20' },
  submitted: { label: 'Submitted', className: 'bg-ndor-success-light text-ndor-success border-ndor-success/20' },
  archived:  { label: 'Archived',  className: 'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge' },
};

const PAGE_SIZE = 25;
// Debounce so we don't spam the URL / API on every keystroke.
const FILTER_DEBOUNCE_MS = 350;

export default function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the source of truth for filter + page state. These derived
  // values re-compute whenever `searchParams` changes — which means
  // back/forward, refresh, and shared links all "just work".
  const companyFromUrl  = searchParams.get('company')     || '';
  const locationFromUrl = searchParams.get('location')    || '';
  const sourceKind      = searchParams.get('source_kind') || 'any';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  // RC 1.46.JOBS-SORT-FILTER (2026-05-02) — three new URL-driven
  // controls: title search (`q`), sort dropdown (`sort`), and
  // decision multi-select (`decision`, comma-separated).
  const titleFromUrl    = searchParams.get('q')           || '';
  const sortFromUrl     = searchParams.get('sort')        || 'newest';
  // Decision multi-select: URL stores comma-separated list. We parse
  // it lazily (in `qp` and the chips render) so React's dep array
  // doesn't see a fresh array reference on every render.
  const decisionStrFromUrl = searchParams.get('decision') || '';
  const decisionsFromUrl = useMemo(
    () => decisionStrFromUrl.split(',').map((s) => s.trim()).filter(Boolean),
    [decisionStrFromUrl],
  );

  // RC 1.48.7 (2026-05-04) — three new URL-driven controls:
  //   has_pack = 'yes' | 'no' | null       — filter jobs by pack presence
  //   from     = 'YYYY-MM-DD' | null       — created_at lower bound
  //   to       = 'YYYY-MM-DD' | null       — created_at upper bound
  // `from` and `to` are inclusive on the calendar day (converted to
  // UTC day boundaries for comparison). Applied client-side against
  // the currently-loaded page so the existing backend contract is
  // untouched. If the page is fully filtered out the empty-state
  // handles the UX.
  const hasPackFromUrl = searchParams.get('has_pack') || 'any';
  const fromFromUrl    = searchParams.get('from')     || '';
  const toFromUrl      = searchParams.get('to')       || '';

  // Text inputs are locally-controlled while the user types; we push
  // their value into the URL after FILTER_DEBOUNCE_MS. If the URL
  // changes externally (back button, clear-filters, shared link), we
  // resync the input — the equality guards on both ends prevent an
  // input ↔ URL ↔ input loop.
  const [companyInput,  setCompanyInput]  = useState(companyFromUrl);
  const [locationInput, setLocationInput] = useState(locationFromUrl);
  const [titleInput,    setTitleInput]    = useState(titleFromUrl);

  useEffect(() => {
    setCompanyInput((v) => (v === companyFromUrl ? v : companyFromUrl));
  }, [companyFromUrl]);
  useEffect(() => {
    setLocationInput((v) => (v === locationFromUrl ? v : locationFromUrl));
  }, [locationFromUrl]);
  useEffect(() => {
    setTitleInput((v) => (v === titleFromUrl ? v : titleFromUrl));
  }, [titleFromUrl]);

  /** Merge-and-write helper. Keys set to `null`/`''` are removed.
   *  Changing a filter always returns to page 1 (avoids stranding the
   *  user on an empty later page after the result set shrinks). */
  const updateParams = useCallback((updates, { resetPage = false } = {}) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([k, v]) => {
          if (v === null || v === undefined || v === '') next.delete(k);
          else next.set(k, String(v));
        });
        if (resetPage) next.delete('page');
        return next;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  // Debounced text → URL. Only writes when the debounced value actually
  // diverges from the URL's current value.
  useEffect(() => {
    const t = setTimeout(() => {
      const v = companyInput.trim();
      if (v !== companyFromUrl) updateParams({ company: v || null }, { resetPage: true });
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [companyInput, companyFromUrl, updateParams]);

  useEffect(() => {
    const t = setTimeout(() => {
      const v = locationInput.trim();
      if (v !== locationFromUrl) updateParams({ location: v || null }, { resetPage: true });
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [locationInput, locationFromUrl, updateParams]);

  // Title search → URL (debounced).
  useEffect(() => {
    const t = setTimeout(() => {
      const v = titleInput.trim();
      if (v !== titleFromUrl) updateParams({ q: v || null }, { resetPage: true });
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [titleInput, titleFromUrl, updateParams]);

  // RC 1.27 — Preferences toggle + dialog state.
  // The toggle persists to localStorage so reloading /jobs doesn't
  // snap the operator back to the un-toggled view. Default OFF so
  // nothing changes for users who don't know the feature exists.
  const [usePrefs, setUsePrefs] = useState(() => {
    try {
      return localStorage.getItem('ndor.jobs.usePreferences') === '1';
    } catch { return false; }
  });
  const toggleUsePrefs = useCallback((next) => {
    setUsePrefs(next);
    try {
      localStorage.setItem('ndor.jobs.usePreferences', next ? '1' : '0');
    } catch { /* noop */ }
  }, []);

  // ── Data ─────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [packsByJobId, setPacksByJobId] = useState({});
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [unfilteredTotal, setUnfilteredTotal] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // RC 1.31 — Free-tier daily Analyze-Fit usage. Drives the usage
  // indicator, the "Daily limit reached" job-card state, and the
  // 429 modal recovery path. Fetched on mount + whenever the jobs
  // list is reloaded so navigating back from /jobs/:id reflects the
  // updated quota.
  const [fitUsage, setFitUsage] = useState(null);
  // RC 1.33 — Plan + usage balance for Focus/Flow users. When the
  // caller is on a paid plan, the indicator / card copy switches
  // from "Free analyses today" to "Included usage remaining".
  const [accountPlan, setAccountPlan] = useState(null);
  const loadUsage = useCallback(async () => {
    try {
      const [u, p] = await Promise.all([
        apiFetch('/api/jobs/analyze-fit/usage'),
        apiFetch('/api/account/plan').catch(() => null),
      ]);
      setFitUsage(u || null);
      setAccountPlan(p || null);
    } catch { /* non-fatal */ }
  }, []);
  useEffect(() => { loadUsage(); }, [loadUsage]);

  const qp = useMemo(() => {
    const qs = new URLSearchParams();
    qs.set('page', String(page));
    qs.set('page_size', String(PAGE_SIZE));
    if (companyFromUrl)  qs.set('company',  companyFromUrl);
    if (locationFromUrl) qs.set('location', locationFromUrl);
    if (titleFromUrl)    qs.set('q',        titleFromUrl);
    if (sourceKind && sourceKind !== 'any') qs.set('source_kind', sourceKind);
    if (sortFromUrl && sortFromUrl !== 'newest') qs.set('sort', sortFromUrl);
    if (decisionsFromUrl.length > 0) qs.set('decision', decisionsFromUrl.join(','));
    if (usePrefs) qs.set('use_preferences', 'true');
    return qs.toString();
  }, [page, companyFromUrl, locationFromUrl, titleFromUrl, sourceKind, sortFromUrl, decisionsFromUrl, usePrefs]);

  const loadJobs = useCallback(async () => {
    const listKey  = KEYS.jobsList(qp);
    const packsKey = KEYS.applicationPacksAll();
    const seed = peek(listKey, 180_000);
    if (!seed.hit) setLoading(true);
    try {
      const [envelope, packsData] = await Promise.all([
        cachedFetch(listKey, () => apiFetch(`/api/jobs?${qp}`), { ttlMs: 180_000 }),
        cachedFetch(packsKey, () => apiFetch('/api/application-packs').catch(() => []), { ttlMs: 180_000 }),
      ]);
      const list = Array.isArray(envelope) ? envelope : (envelope?.items || []);
      setItems(list);
      setTotal(envelope?.total ?? list.length);
      setUnfilteredTotal(
        (envelope && Object.prototype.hasOwnProperty.call(envelope, 'unfiltered_total'))
          ? envelope.unfiltered_total
          : null,
      );
      setHasNext(Boolean(envelope?.has_next));
      setHasPrev(Boolean(envelope?.has_prev));

      // RC 1.49.4 — packsData may itself be an envelope from
      // /api/application-packs (Slice A returns {items, total}).
      // Normalise before indexing.
      const packsList = Array.isArray(packsData)
        ? packsData
        : (packsData?.items || []);
      const byJob = {};
      for (const p of packsList) {
        if (p?.job_id && !byJob[p.job_id]) byJob[p.job_id] = p;
      }
      setPacksByJobId(byJob);
    } catch {
      setItems([]); setTotal(0); setHasNext(false); setHasPrev(false);
    } finally {
      setLoading(false);
    }
  }, [qp]);

  useEffect(() => { loadJobs(); loadUsage(); }, [loadJobs, loadUsage]);

  // RC 1.48.7 — client-side filters: has_pack, from, to.
  // Applied AFTER the server-side slice so we don't double-count or
  // break pagination totals. `displayItems` drives the list render;
  // `total` / pagination bar still reflect the full server-side
  // slice (documented via the "Showing X of Y" indicator).
  const fromTs = useMemo(() => {
    if (!fromFromUrl) return null;
    const d = new Date(fromFromUrl + 'T00:00:00Z');
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }, [fromFromUrl]);
  const toTs = useMemo(() => {
    if (!toFromUrl) return null;
    const d = new Date(toFromUrl + 'T23:59:59Z');
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }, [toFromUrl]);

  const displayItems = useMemo(() => {
    // RC 1.54 (2026-05-12 PM, operator-locked) — Active queue
    // cleanup. By default, jobs whose latest analyse-fit decision is
    // `reject` are HIDDEN from the user's active queue. They remain
    // in the database and remain reachable via the explicit "Reject"
    // decision chip — this is purely a presentation-layer queue
    // exclusion, NOT deletion / archival / DB write. Frontend-only.
    const showRejected = decisionsFromUrl.includes('reject');
    return items.filter((job) => {
      // has_pack filter.
      if (hasPackFromUrl === 'yes' && !packsByJobId[job.id]) return false;
      if (hasPackFromUrl === 'no'  &&  packsByJobId[job.id]) return false;
      // Date range filter on created_at.
      if (fromTs || toTs) {
        const created = job.created_at ? new Date(job.created_at).getTime() : null;
        if (!created || Number.isNaN(created)) return false;
        if (fromTs && created < fromTs) return false;
        if (toTs   && created > toTs)   return false;
      }
      // RC 1.54 — reject queue exclusion. The user must explicitly
      // tick the "Reject" decision chip to bring rejected items back
      // into the visible queue.
      if (!showRejected && job?.user_score?.final_decision === 'reject') {
        return false;
      }
      return true;
    });
  }, [items, packsByJobId, hasPackFromUrl, fromTs, toTs, decisionsFromUrl]);

  // RC 1.48.7 — bulk "Start Pack" candidate list. Jobs in the
  // currently-visible slice that have an analyse-fit score but NO
  // pack yet. Surfaced only when 2+ such jobs are on screen —
  // single-job case is already one click away via the job card.
  const bulkPackCandidates = useMemo(
    () => displayItems.filter(
      (j) => !!j.user_score && !packsByJobId[j.id],
    ),
    [displayItems, packsByJobId],
  );

  const filtersActive = Boolean(
    companyFromUrl || locationFromUrl || titleFromUrl
    || (sourceKind && sourceKind !== 'any')
    || (sortFromUrl && sortFromUrl !== 'newest')
    || decisionsFromUrl.length > 0
    // RC 1.48.7 — new client-side filters participate in the
    // "Clear filters" surface so the operator can reset them all.
    || (hasPackFromUrl && hasPackFromUrl !== 'any')
    || fromFromUrl || toFromUrl,
  );
  // RC 1.49.x Slice 6 — progressive disclosure of the secondary
  // filter row. Auto-expanded when any of the wrapped filters are
  // currently active so the user sees their applied filters; calm
  // and collapsed by default for first-time arrival.
  const secondaryFiltersActive = Boolean(
    companyFromUrl
    || (sourceKind && sourceKind !== 'any')
    || (hasPackFromUrl && hasPackFromUrl !== 'any')
    || fromFromUrl || toFromUrl,
  );
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(secondaryFiltersActive);
  // Re-sync if URL params change externally (back/forward).
  useEffect(() => {
    if (secondaryFiltersActive) setMoreFiltersOpen(true);
  }, [secondaryFiltersActive]);
  const clearFilters = () => {
    // Clear everything. Same effect as navigating to /jobs with no
    // query string — but we keep it an imperative click for
    // discoverability.
    setSearchParams(new URLSearchParams());
  };

  const pageStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd   = total === 0 ? 0 : Math.min(page * PAGE_SIZE, total);

  return (
    <div data-testid="jobs-page" className="space-y-6 min-w-0 max-w-full overflow-x-hidden">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-ndor-ink font-outfit">Jobs</h2>
          <p className="text-sm text-ndor-ink-secondary mt-1 font-manrope" data-testid="jobs-total-count">
            {total > 0
              ? `${total.toLocaleString()} job${total === 1 ? '' : 's'}${filtersActive ? ' matching filters' : ' tracked'}`
              : (filtersActive ? 'No jobs match these filters.' : 'Add jobs to start analysing your fit.')}
          </p>
        </div>
        <AddJobDialog onJobAdded={() => {
          // RC 1.49.4 — new job invalidates the jobs-list cache
          // family (every keyed query string) so the just-imported
          // row appears immediately.
          cacheInvalidate(['jobs', 'application-packs:all']);
          updateParams({}, { resetPage: true });
          loadJobs();
        }} />
      </div>

      {/* RC 1.27 — preference-aware scoring controls */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 rounded border border-ndor-edge bg-ndor-surface-alt/40"
        data-testid="jobs-preferences-bar"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <Switch
            id="jobs-use-preferences"
            checked={usePrefs}
            onCheckedChange={toggleUsePrefs}
            data-testid="jobs-use-preferences-toggle"
          />
          <label
            htmlFor="jobs-use-preferences"
            className="text-sm text-ndor-ink cursor-pointer select-none"
          >
            {usePrefs ? 'Filtered by your preferences' : 'Showing all jobs'}
          </label>
          {usePrefs && (
            <span
              className="text-xs text-ndor-ink-muted"
              data-testid="jobs-preference-filter-note"
            >
              {(typeof unfilteredTotal === 'number')
                ? `Filtering by your preferences — showing ${total.toLocaleString()} of ${unfilteredTotal.toLocaleString()} jobs.`
                : 'Filtering by your preferences'}
            </span>
          )}
          {/* RC 1.33 — Plan-aware usage indicator.
              Paid (Focus/Flow) active users see their included + extra
              balance; everyone else keeps the RC 1.31 daily counter.
              RC 1.33.FIX — values are clamped client-side so a stale
              or over-counted backend response can never render
              "4 / 2" or a negative balance. */}
          {(() => {
            const isPaid = accountPlan
              && (accountPlan.plan_code === 'focus' || accountPlan.plan_code === 'flow')
              && accountPlan.status === 'active';
            if (isPaid) {
              const included = Math.max(0, accountPlan.included_usage_remaining || 0);
              const extra    = Math.max(0, accountPlan.extra_usage_remaining    || 0);
              return (
                <span
                  className="text-xs text-ndor-ink-muted font-mono-ui tabular-nums"
                  data-testid="jobs-included-usage"
                  title="Included Credits remaining this cycle. Re-opening an already analysed job doesn't count."
                >
                  Included Credits remaining: {included}
                  {extra > 0 ? ` (+${extra} extra)` : ''}
                </span>
              );
            }
            if (fitUsage) {
              const limit        = Math.max(0, fitUsage.limit || 0);
              const usedDisplay  = Math.min(Math.max(0, fitUsage.used || 0), limit);
              return (
                <span
                  className="text-xs text-ndor-ink-muted font-mono-ui tabular-nums"
                  data-testid="jobs-analyze-fit-usage"
                  title="New jobs you've analysed today. Re-opening an already analysed job doesn't count."
                >
                  Free analyses used: {usedDisplay} / {limit}
                </span>
              );
            }
            return null;
          })()}
        </div>
        {/* RC 1.44.PREF-1 — Single source of truth.
            The Jobs-page PreferencesDialog (4-field subset) was
            retired in favour of the canonical Account → Preferences
            editor. Both editors always wrote to the same
            `profiles.preferences_json` column via the same
            `POST /api/profile/preferences` endpoint, so this
            consolidation is a UI cleanup with zero data risk:
            • Hard filter (`passes_preference_filter`) still reads
              the same column.
            • LLM Analyse Fit prompt block
              (`format_candidate_preferences_block`) still reads
              the same column.
            • The hydration banner below this bar already deep-links
              users to /account?tab=preferences for editing.
            See docs/system_state/rc_1_44_pref_1_preferences_surface_consolidation.md */}
        <Button
          asChild
          variant="outline" size="sm"
          data-testid="jobs-open-preferences"
        >
          <Link to="/account?tab=preferences">
            Manage preferences in Account → Preferences
          </Link>
        </Button>
      </div>

      {/* RC 1.43.B — Unified preference hydration banner. */}
      <JobsPreferencesHydrationBanner />

      {/* Filter bar */}
      <Card className="border-ndor-edge shadow-sm" data-testid="jobs-filter-bar">
        <CardContent className="p-3 sm:p-4 space-y-3">
          {/*
            RC 1.49.x Slice 6 — Reviewer-trust progressive disclosure.
            Primary always-visible filters (Search title · Location ·
            Sort) live in the top row. Secondary filters (Company ·
            Source · Pack · From · To) collapse into a single "More
            filters" disclosure to reduce first-time visual density
            without removing capability. Mobile gets the same
            collapsed-by-default treatment.

            RC 1.58 Phase 3F (operator-locked, 2026-02-14) —
            Presentation cleanup. Primary row is now a balanced
            responsive grid (1 col mobile / 2 col tablet / 3 col
            laptop+) with equal-width controls and aligned heights;
            secondary panel grows from 1 col → 2 → 3 → 5 across the
            same breakpoints. Clear button relocated to the
            disclosure-toggle row so the input row stays uncluttered.
            Pure layout — filtering logic / state / endpoints
            unchanged.
          */}
          <div
            data-testid="jobs-filter-primary-row"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
          >
            <div>
              <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-title">
                Search title
              </label>
              <Input
                id="jobs-filter-title"
                data-testid="jobs-filter-title"
                placeholder="e.g. Senior Engineer"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-location">
                Location
              </label>
              <Input
                id="jobs-filter-location"
                data-testid="jobs-filter-location"
                placeholder="e.g. Dublin"
                value={locationInput}
                onChange={(e) => setLocationInput(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-sort">
                Sort by
              </label>
              <Select
                value={sortFromUrl}
                onValueChange={(v) => updateParams({ sort: v === 'newest' ? null : v }, { resetPage: true })}
              >
                <SelectTrigger id="jobs-filter-sort" data-testid="jobs-filter-sort" className="mt-1">
                  <SelectValue placeholder="Newest" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest" data-testid="jobs-filter-sort-newest">Newest first</SelectItem>
                  <SelectItem value="oldest" data-testid="jobs-filter-sort-oldest">Oldest first</SelectItem>
                  <SelectItem value="fit_desc" data-testid="jobs-filter-sort-fit-desc">Fit: high to low</SelectItem>
                  <SelectItem value="fit_asc" data-testid="jobs-filter-sort-fit-asc">Fit: low to high</SelectItem>
                  <SelectItem value="company_asc" data-testid="jobs-filter-sort-company-asc">Company A→Z</SelectItem>
                  <SelectItem value="company_desc" data-testid="jobs-filter-sort-company-desc">Company Z→A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* RC 1.49.x Slice 6 — secondary filters disclosure trigger.
              RC 1.58 Phase 3F — Clear filters CTA hoisted into this
              row, right-aligned, so the primary input row stays a
              clean balanced grid. */}
          <div
            className="flex flex-wrap items-center justify-between gap-2"
            data-testid="jobs-filter-disclosure-row"
          >
            <button
              type="button"
              onClick={() => setMoreFiltersOpen((v) => !v)}
              data-testid="jobs-filter-more-toggle"
              aria-expanded={moreFiltersOpen}
              aria-controls="jobs-filter-more-panel"
              className="inline-flex items-center gap-1.5 text-xs text-ndor-ink-secondary hover:text-ndor-ink transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-ndor-edge rounded px-1 py-0.5"
            >
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${moreFiltersOpen ? 'rotate-180' : ''}`}
              />
              {moreFiltersOpen ? 'Hide filters' : 'More filters'}
              {!moreFiltersOpen && secondaryFiltersActive && (
                <span
                  className="ml-1 inline-flex items-center rounded-full bg-ndor-info-light px-1.5 py-0 text-[10px] font-medium text-ndor-info"
                  data-testid="jobs-filter-more-active-badge"
                >
                  Active
                </span>
              )}
            </button>
            {filtersActive && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                data-testid="jobs-filter-clear"
                className="text-ndor-ink-muted shrink-0"
              >
                <X className="h-3.5 w-3.5 mr-1" /> Clear filters
              </Button>
            )}
          </div>

          {moreFiltersOpen && (
            <div
              id="jobs-filter-more-panel"
              data-testid="jobs-filter-more-panel"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 pt-3 border-t border-ndor-edge"
            >
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-company">
                  Company
                </label>
                <Input
                  id="jobs-filter-company"
                  data-testid="jobs-filter-company"
                  placeholder="e.g. Stripe"
                  value={companyInput}
                  onChange={(e) => setCompanyInput(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-kind">
                  Source
                </label>
                <Select
                  value={sourceKind}
                  onValueChange={(v) => updateParams({ source_kind: v === 'any' ? null : v }, { resetPage: true })}
                >
                  <SelectTrigger id="jobs-filter-kind" data-testid="jobs-filter-source-kind" className="mt-1">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any" data-testid="jobs-filter-source-any">Any source</SelectItem>
                    <SelectItem value="platform" data-testid="jobs-filter-source-platform">Platform (public)</SelectItem>
                    <SelectItem value="user_import" data-testid="jobs-filter-source-user">My imports</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* RC 1.48.7 — Has Pack filter. Client-side filter on
                  packsByJobId; URL-driven so back/refresh/link preserves. */}
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-has-pack">
                  Pack
                </label>
                <Select
                  value={hasPackFromUrl}
                  onValueChange={(v) => updateParams({ has_pack: v === 'any' ? null : v }, { resetPage: true })}
                >
                  <SelectTrigger id="jobs-filter-has-pack" data-testid="jobs-filter-has-pack" className="mt-1">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any" data-testid="jobs-filter-has-pack-any">Any</SelectItem>
                    <SelectItem value="yes" data-testid="jobs-filter-has-pack-yes">Has pack</SelectItem>
                    <SelectItem value="no"  data-testid="jobs-filter-has-pack-no">No pack yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* RC 1.48.7 — Date range (created_at). Inclusive on the
                  calendar day; empty inputs = unbounded. */}
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-from">
                  From
                </label>
                <Input
                  id="jobs-filter-from"
                  type="date"
                  value={fromFromUrl}
                  onChange={(e) => updateParams({ from: e.target.value || null }, { resetPage: true })}
                  data-testid="jobs-filter-from"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted" htmlFor="jobs-filter-to">
                  To
                </label>
                <Input
                  id="jobs-filter-to"
                  type="date"
                  value={toFromUrl}
                  onChange={(e) => updateParams({ to: e.target.value || null }, { resetPage: true })}
                  data-testid="jobs-filter-to"
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {/* RC 1.46.JOBS-SORT-FILTER — decision multi-chip filter.
              Click a chip to toggle that decision in/out of the
              filter set. Empty set = "show all decisions" (default). */}
          <div
            className="flex flex-wrap items-center gap-2 pt-1"
            data-testid="jobs-filter-decision-chips"
          >
            <span className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted mr-1">
              Decision
            </span>
            {[
              { key: 'shortlist',                label: 'Shortlist' },
              { key: 'apply_with_repositioning', label: 'Tailor' },
              { key: 'hold',                     label: 'Hold' },
              { key: 'reject',                   label: 'Reject' },
              { key: 'unanalysed',               label: 'Unanalysed' },
            ].map((d) => {
              const active = decisionsFromUrl.includes(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => {
                    const next = active
                      ? decisionsFromUrl.filter((x) => x !== d.key)
                      : [...decisionsFromUrl, d.key];
                    updateParams(
                      { decision: next.length > 0 ? next.join(',') : null },
                      { resetPage: true },
                    );
                  }}
                  data-testid={`jobs-filter-decision-${d.key}`}
                  data-active={active ? 'true' : 'false'}
                  aria-pressed={active}
                  className={
                    'px-2.5 py-1 rounded-full border text-xs font-medium transition-colors '
                    + (active
                      ? 'bg-ndor-ink text-ndor-surface border-ndor-ink'
                      : 'bg-ndor-surface text-ndor-ink-secondary border-ndor-edge hover:border-ndor-ink/40 hover:text-ndor-ink')
                  }
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-16" data-testid="jobs-loading">
          <Loader2 className="h-6 w-6 animate-spin text-ndor-ink-muted" />
        </div>
      )}

      {/* Empty state */}
      {!loading && displayItems.length === 0 && (
        <Card className="border-ndor-edge shadow-sm border-dashed" data-testid="jobs-empty-state">
          <CardContent className="p-12 text-center">
            <Briefcase className="h-10 w-10 text-ndor-ink-muted mx-auto mb-4" />
            {usePrefs && !filtersActive ? (
              <>
                <p
                  className="text-sm font-medium text-ndor-ink"
                  data-testid="jobs-empty-state-prefs-title"
                >
                  No jobs match your preferences
                </p>
                <p className="text-xs text-ndor-ink-muted mt-1.5 max-w-sm mx-auto">
                  Adjust your preferences or view all jobs.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => toggleUsePrefs(false)}
                  data-testid="jobs-empty-state-show-all"
                >
                  Show all jobs
                </Button>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-ndor-ink">
                  {filtersActive ? 'No jobs match these filters' : 'No jobs yet'}
                </p>
                <p className="text-xs text-ndor-ink-muted mt-1.5 max-w-xs mx-auto">
                  {filtersActive
                    ? 'Try a broader company, location, or source. Jobs with an active application pack are tracked on the Applications page.'
                    : 'Add a job by pasting a link or description, then run Source CV Baseline Fit to see how well your Source CV matches before tailoring.'}
                </p>
                {/* RC 1.49.x Slice 7 — calm reviewer-guidance hint
                    on the no-jobs path. Sequencing only — not a
                    walkthrough or product tour. Renders only on
                    the genuine empty state (no filters active),
                    so existing-user filtered-empty UX is untouched. */}
                {!filtersActive && (
                  <div
                    className="mt-5 mx-auto max-w-md rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-3 text-left"
                    data-testid="jobs-empty-state-reviewer-hint"
                  >
                    <p className="text-[10px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
                      Recommended next step
                    </p>
                    <ol className="mt-2 space-y-1.5 text-xs text-ndor-ink-secondary leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-ndor-ink">1.</span>
                        <span>
                          <Link to="/documents" className="text-ndor-info hover:underline">Upload your primary CV</Link>
                          {' '}from your Documents page if you haven&apos;t already.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-ndor-ink">2.</span>
                        <span>Click <span className="font-medium text-ndor-ink">Add Job</span> above to import a role by pasting its link or description.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-ndor-ink">3.</span>
                        <span>Run <span className="font-medium text-ndor-ink">Analyse Fit</span> on the imported job to see how well your CV matches.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-medium text-ndor-ink">4.</span>
                        <span>Generate a tailored CV, cover letter, and apply-ready application pack.</span>
                      </li>
                    </ol>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* RC 1.48.7 — Bulk "Start Pack" CTA. Surfaces when 2+ visible
          jobs have analyse-fit scores but no pack yet. Clicking
          jumps to the first such job where the wizard's Start-Pack
          flow will queue. Not a multi-select/batch action — that
          would cross the canonical pack-generation contract. */}
      {!loading && bulkPackCandidates.length >= 2 && (
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-ndor-edge bg-ndor-info-light/60 px-3 py-2"
          data-testid="jobs-bulk-start-pack-cta"
        >
          <p className="text-xs text-ndor-ink-secondary leading-relaxed">
            <span className="font-medium text-ndor-ink" data-testid="jobs-bulk-start-pack-count">
              {bulkPackCandidates.length}
            </span>{' '}
            analysed jobs on this page don&apos;t yet have an application pack.
            Build one now to turn analysis into an apply-ready bundle.
          </p>
          <Button
            asChild
            size="sm"
            variant="outline"
            className="shrink-0 border-ndor-info/30 text-ndor-info hover:bg-ndor-surface"
            data-testid="jobs-bulk-start-pack-button"
          >
            <Link to={`/jobs/${bulkPackCandidates[0].id}?startPack=1`}>
              <Package className="h-3.5 w-3.5 mr-1.5" /> Start pack on the first
            </Link>
          </Button>
        </div>
      )}

      {/* Job list */}
      {!loading && displayItems.length > 0 && (
        <div className="space-y-3" data-testid="jobs-list">
          {displayItems.map((job) => {
            const score = job.user_score;
            const decisionBadge = score ? DECISION_BADGES[score.final_decision] : null;
            const pack = packsByJobId[job.id];
            const packBadge = pack ? PACK_STATUS_BADGES[pack.pack_status] : null;
            const prefScoring = job.preference_scoring;
            const matchTier = matchTierOf(prefScoring?.match_label);
            const matchTone = matchTier ? MATCH_TIER_TONE[matchTier] : null;

            return (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className="block group"
                data-testid={`job-row-${job.id}`}
              >
                <Card className="border-ndor-edge shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4 sm:p-5">
                    {/* RC 1.56 P1 (operator-locked, 2026-05-13 PM) —
                        Mobile card-pattern parity with ApplicationCard.
                        On <sm widths the title/metadata block and the
                        pill block STACK vertically; pills get
                        `flex-wrap` so they layout naturally below the
                        title rather than overflowing the right edge.
                        On sm+ the two-column layout is preserved. */}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-medium text-ndor-ink group-hover:underline truncate">
                            {job.title || 'Untitled Job'}
                          </h3>
                          {job.source_kind === 'user_import' && (
                            <span
                              className="inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-ndor-edge bg-ndor-surface-alt/70 text-ndor-ink-secondary font-mono-ui"
                              data-testid="job-private-pill"
                              title="This job is private to your account"
                            >
                              Private
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-ndor-ink-muted">
                          {job.company && (
                            <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> {job.company}</span>
                          )}
                          {job.location && (
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.location}</span>
                          )}
                          {job.employment_type && (
                            <span className="capitalize">{job.employment_type.replace('_', ' ')}</span>
                          )}
                          {job.seniority_level && (
                            <span className="capitalize">{job.seniority_level}</span>
                          )}
                        </div>
                      </div>

                      {/* RC 1.56 P1 — pill block now WRAPS instead
                          of overflowing. `shrink-0` dropped so pills
                          can break to a new line; `flex-wrap` lets
                          the analysed/match/decision chips stack
                          gracefully. Mirrors ApplicationCard.jsx:733. */}
                      <div className="flex items-center gap-2 flex-wrap min-w-0 sm:justify-end sm:shrink-0">
                        {/* RC 1.48.7 — Variant-aware status chip.
                            Surfaces the apply-readiness stage of each job:
                              • Pack      — application_packs row exists
                              • Tailored  — pack row has cv_variant_id
                                            (rendered as "Pack" already; kept
                                            separate for future variant-only
                                            case — today we distinguish
                                            Pack(draft) vs Pack(finalised)
                                            via packBadge.label below)
                              • Analysed  — user_score exists, no pack
                              • Unanalysed — neither
                            Rendered as a single small pill so it doesn't
                            crowd the existing score + decision chips. */}
                        {(() => {
                          const hasPack    = !!packsByJobId[job.id];
                          const hasAnalysis = !!job.user_score;
                          if (hasPack) return null; // packBadge renders instead
                          if (hasAnalysis) {
                            return (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-ndor-success/20 bg-ndor-success-light text-ndor-success font-mono-ui"
                                data-testid={`job-row-stage-analysed-${job.id}`}
                                title="Source CV analysed for this role"
                              >
                                <Sparkles className="h-3 w-3" /> Analysed
                              </span>
                            );
                          }
                          return (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.16em] px-1.5 py-0.5 rounded border border-ndor-edge bg-ndor-surface-alt/70 text-ndor-ink-muted font-mono-ui"
                              data-testid={`job-row-stage-unanalysed-${job.id}`}
                            >
                              New
                            </span>
                          );
                        })()}
                        {packBadge && (
                          <Badge
                            variant="outline"
                            className={`text-[10px] flex items-center gap-1 ${packBadge.className}`}
                            data-testid={`pack-progress-${job.id}`}
                            title={`Application pack: ${packBadge.label}`}
                          >
                            <Package className="h-3 w-3" /> {packBadge.label}
                          </Badge>
                        )}
                        {score ? (
                          <>
                            <span
                              className="inline-flex items-baseline gap-1 px-2 py-0.5 rounded border border-ndor-edge bg-ndor-surface-alt/70 shrink-0"
                              title="Last analysed fit — the CV you last ran Analyse Fit with, vs this job. Application packs may show a different, pack-aware fit once a Tailored CV and cover letter are assigned."
                              data-testid={`job-row-fit-${job.id}`}
                            >
                              <span className="text-[9px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
                                Analysed
                              </span>
                              <span className="text-base font-semibold font-outfit text-ndor-ink tabular-nums">
                                {Math.round((score.fit_score || 0) * 100)}
                              </span>
                            </span>
                            {usePrefs && prefScoring?.final_score !== null && prefScoring?.final_score !== undefined && (
                              <span
                                className="inline-flex items-baseline gap-1 px-2 py-0.5 rounded border border-ndor-edge bg-ndor-surface shrink-0"
                                title={
                                  `Preference-adjusted score = base ${prefScoring.base_score}`
                                  + ` × location multiplier ${prefScoring.location_multiplier}`
                                }
                                data-testid={`job-row-match-score-${job.id}`}
                              >
                                <span className="text-[9px] font-mono-ui uppercase tracking-[0.16em] text-ndor-ink-muted">
                                  Match
                                </span>
                                <span className="text-base font-semibold font-outfit text-ndor-ink tabular-nums">
                                  {Math.round(prefScoring.final_score)}
                                </span>
                              </span>
                            )}
                            {usePrefs && matchTier && (
                              <span className="inline-flex items-center gap-1">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${matchTone}`}
                                  title={prefScoring.match_label}
                                  data-testid={`job-row-match-label-${job.id}`}
                                >
                                  {prefScoring.match_label}
                                </Badge>
                                <MatchExplanationPopover
                                  preferenceScoring={prefScoring}
                                  triggerTestId={`job-row-why-${job.id}`}
                                />
                              </span>
                            )}
                            {decisionBadge && (
                              <DecisionAdvicePopover
                                advice={
                                  score.final_decision === 'apply_with_repositioning'
                                    ? score.repositioning_advice
                                    : score.final_decision === 'shortlist'
                                      ? score.acceptance_reason
                                      : score.final_decision === 'reject'
                                        ? score.rejection_reason
                                        : score.final_decision === 'hold'
                                          ? score.hold_reason
                                          : null
                                }
                                decision={score.final_decision}
                                confidence={score.decision_confidence}
                                badgeLabel={decisionBadge.label}
                                className={decisionBadge.className}
                                testIdBase={`job-row-advice-${job.id}`}
                              />
                            )}
                          </>
                        ) : (
                          (() => {
                            // RC 1.33 — Paid plan exhaustion pill.
                            // RC 1.44.H2 (2026-04-30) — P0 commercial-trust fix.
                            // Base users with free-tier exhausted but wallet credits
                            // available were wrongly shown "Daily limit reached";
                            // they can still spend their purchased credits. Badge
                            // now only shows when BOTH are empty.
                            const isPaid = accountPlan
                              && (accountPlan.plan_code === 'focus' || accountPlan.plan_code === 'flow')
                              && accountPlan.status === 'active';
                            const includedRemaining = Number(accountPlan?.included_usage_remaining || 0);
                            const extraRemaining    = Number(accountPlan?.extra_usage_remaining    || 0);
                            const walletHasCredits  = includedRemaining > 0 || extraRemaining > 0;
                            const paidExhausted     = isPaid && !walletHasCredits;
                            if (paidExhausted) {
                              // RC 1.48.7 Scope B — inline upgrade CTA.
                              // Paid plan users with zero wallet Credits get
                              // a direct link to the top-up flow so they
                              // don't have to click the modal to discover it.
                              // preventDefault on the outer <Link> → we use
                              // a nested <Link> that stops propagation so
                              // the card's overall navigation still works.
                              return (
                                <span
                                  className="inline-flex items-center gap-1"
                                  data-testid={`job-row-usage-exhausted-${job.id}`}
                                >
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] text-ndor-ink-muted border-ndor-edge"
                                    title="You've used your included Credits."
                                  >
                                    Credits exhausted
                                  </Badge>
                                  <Link
                                    to="/account?tab=plan"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[10px] font-semibold text-ndor-primary hover:underline"
                                    data-testid={`job-row-add-credits-link-${job.id}`}
                                  >
                                    Add Credits →
                                  </Link>
                                </span>
                              );
                            }
                            if (!isPaid && fitUsage && fitUsage.remaining === 0 && !walletHasCredits) {
                              const limit = Math.max(1, Number(fitUsage.limit) || 2);
                              return (
                                <span
                                  className="inline-flex items-center gap-1"
                                  data-testid={`job-row-limit-reached-${job.id}`}
                                >
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] text-ndor-ink-muted border-ndor-edge"
                                    title={`You've used your ${limit} free job analyses for today and have no top-up Credits.`}
                                  >
                                    Daily limit reached
                                  </Badge>
                                  <Link
                                    to="/account?tab=plan"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-[10px] font-semibold text-ndor-primary hover:underline"
                                    data-testid={`job-row-upgrade-link-${job.id}`}
                                  >
                                    Top up →
                                  </Link>
                                </span>
                              );
                            }
                            return (
                              <Badge variant="outline" className="text-[10px] text-ndor-ink-muted border-ndor-edge">
                                <Sparkles className="h-3 w-3 mr-1" /> Not analysed
                              </Badge>
                            );
                          })()
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      {/* Pagination bar */}
      {!loading && items.length > 0 && (hasPrev || hasNext) && (
        <div
          className="flex items-center justify-between pt-2"
          data-testid="jobs-pagination"
        >
          <p className="text-xs text-ndor-ink-muted font-mono-ui" data-testid="jobs-page-state">
            Showing <span className="tabular-nums">{pageStart.toLocaleString()}</span>–
            <span className="tabular-nums">{pageEnd.toLocaleString()}</span>
            {' '}of <span className="tabular-nums">{total.toLocaleString()}</span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => updateParams({ page: Math.max(1, page - 1) })}
              disabled={!hasPrev}
              data-testid="jobs-page-prev"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <span className="text-xs text-ndor-ink-muted font-mono-ui" data-testid="jobs-page-indicator">
              Page {page}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => updateParams({ page: page + 1 })}
              disabled={!hasNext}
              data-testid="jobs-page-next"
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
